import THREE from "three";
import { USE_MULTI_MATERIAL_RENDER_CALLS, USE_OUT_OF_CORE_TILE_MANAGER } from "../../globals";
import { getVertexCount } from "../VertexEnumerator";
import { getByteSize } from "../BufferGeometryUtils";
import { ParallelGeomMerge } from "./ParallelGeomMerge";
import { logger } from "../../../logger/Logger";
import { MATERIAL_VARIANT } from "../../render/MaterialManager";
import { MeshFlags } from "../MeshFlags";
import { RenderFlags } from "../RenderFlags";
import { OtgLoader } from "../../../file-loaders/main/OtgLoader";
import { RcsFileLoader } from "../../../file-loaders/rcs/RcsFileLoader";
import { materialsAreUBOCompatible, getMaxMaterialsPerUBO, isSupportedUBOMaterial, MAX_FRAGMENTS_PER_CONSOLIDATED_MESH } from "../../render/UniformBufferObjects";
import { canBeMerged, mergeGeometries, MaxVertexCountPerMesh } from "./ConsolidationCommon";

export const NO_MESH_FOR_FRAGMENT = -1;
export const MESH_STILL_PENDING = -2;
export const MIN_VALUE_FOR_PENDING_RANGE = MESH_STILL_PENDING - 1;


/** @import { RenderModel } from "../RenderModel" */
/** @import { FragmentList } from "../FragmentList" */

/**
 * Can we use the earlier consolidation?
 * This is only possible if the OutOfCoreTileManager is enabled and we are using the OtgLoader.
 *
 * The traditional consolidation algorithm was based on the assumption that the memory is statically partitioned
 * into a part that is used for the consolidations and the rest is used to upload the geometries. So the algorithm
 * had to decide which geometries can be consolidated to fit into this global memory limit (across all geometries).
 * This meant, that the consolidation could only be performed once all geometries were loaded, since we needed to know
 * the memory requirements of all geometries to decide which ones would still fit into the global consolidation memory.
 *
 * With the OutOfCoreTileManager, we now manage the memory dynamically and no longer decide globally which geometries
 * can be consolidated. Instead, we make the decision for each of the BVH nodes separately. This allows us to change the
 * criterion from a global criterion (i.e. half of the available memory for all geometries), to a local criterion (the
 * number of triangles in each of the geometries).
 *
 * We now are able to do the computation of the Consolidation Map (the actual consolidation is deferred to a task in the
 * OutOfCoreTileManager) once we know for all fragments the geometryIDs and materials (those are stored in the fragment list)
 * as well as the bounding boxes, transparency flags and polycount (from the fragments extra data).
 *
 * @param {RenderModel} model - The model
 * @returns
 */
export function useEarlyConsolidation(model) {
  return USE_OUT_OF_CORE_TILE_MANAGER && model.loader instanceof OtgLoader && !model.loader.svf.loadDone;
}

/**
 *  With the OtgLoader and RcsFileLoader, incremental consolidation can start before the model is fully loaded. For others we need to wait.
 */
export function needToWaitForGeometry(model) {
  return !(model.loader instanceof OtgLoader || model.loader instanceof RcsFileLoader);
}

export function isSingleFragMesh(mesh) {
  return mesh.fragId !== undefined;
}

export function isPlaceholderMesh(mesh) {
  return !mesh.geometry?.attributes && mesh.fragId === undefined;
}

export function isInstanceMesh(mesh) {
  return !!mesh.geometry?.numInstances;
}

export function isConsolidatedMesh(mesh) {
  return !isInstanceMesh(mesh) && !!mesh.geometry?.attributes?.id;
}

var MESH_HIGHLIGHTED = MeshFlags.MESH_HIGHLIGHTED;
var flagMask = MeshFlags.MESH_VISIBLE | MeshFlags.MESH_HIDE | MESH_HIGHLIGHTED;
var flagVisible = MeshFlags.MESH_VISIBLE;
var flagHiddenMask = MeshFlags.MESH_VISIBLE | MeshFlags.MESH_HIDE;
var flagHiddenVisible = 0;
var flagHighlightMask = MeshFlags.MESH_HIGHLIGHTED | MeshFlags.MESH_HIDE;
var flagHighlightVisible = MeshFlags.MESH_HIGHLIGHTED;
var RENDER_HIDDEN = RenderFlags.RENDER_HIDDEN;
var RENDER_HIGHLIGHTED = RenderFlags.RENDER_HIGHLIGHTED;

// Should the object with flags get drawn in this render pass.
export function isVisible(flags, drawMode) {
  switch (drawMode) {
    case RENDER_HIDDEN:
      return (flags & flagHiddenMask) === flagHiddenVisible; //Ghosted not visible and not hidden
    case RENDER_HIGHLIGHTED:
      return (flags & flagHighlightMask) === flagHighlightVisible; //highlighted (bit 1 on)
  }
  return (flags & flagMask) == flagVisible; //visible but not highlighted, and not a hidden line (bit 0 on, bit 1 off, bit 2 off)
}

const _tmpMatrix = new THREE.Matrix4();

/**
  *  Helper class to collect shapes with identical materials and merge them into a single large shape.
  *
  *  @constructor
  *    @param {number} materialId - Material must be the same for all added geometries.
  *    @param {number} bvhNodeId - BVH node id of the bucket
  */
function MergeBucket(materialId, bvhNodeId) {
  this.geoms = [];
  this.matrices = [];
  this.vertexCount = 0;
  this.materialId = materialId;
  this.fragIds = [];
  this.worldBox = new THREE.Box3();
  this.cost = 0;
  this.bvhNodeId = bvhNodeId;
}

MergeBucket.prototype = {
  constructor: MergeBucket,

  /**
   * @param {THREE.BufferGeometry} geom
   * @param {THREE.Box3}           worldBox
   * @param {Number}               fragId
   * @returns {Number}             costs - memory cost increase caused by the new geometry
   */
  addGeom: function (geom, worldBox, fragId) {

    this.fragIds.push(fragId);

    this.worldBox.union(worldBox);

    if (geom === null) {
      return 0;
    }

    this.geoms.push(geom);
    this.vertexCount += getVertexCount(geom);

    // Track memory costs. As long as the bucket has only a single shape,
    // we have no costs at all.
    var numGeoms = this.geoms.length;
    if (numGeoms == 1) {
      return 0;
    }

    // Fragment geometries are usually BufferGeometry, which provide a byteSize for the
    // interleaved buffer. Anything else is currently unexpected and needs code change.
    if (geom.byteSize === undefined) {
      logger.warn("Error in consolidation: Geometry must contain byteSize.");
    }

    // For any bucket with >=2 geoms, all geometries must be considered for the costs.
    let costDelta = geom.byteSize + (numGeoms == 2 ? this.geoms[0].byteSize : 0);
    this.cost += costDelta;

    return costDelta;
  }
};

/**
 * @class Helper class to collect results of ConsolidationBuilder.
 * @param {number} fragCount - number of fragments in the model
 * @param {number} modelId - model id
 */
export function Consolidation(fragCount, modelId) {

  // all consolidated meshes (+ some original geometries if they could not be merged)
  // Note: the entries in this array are no longer necessarily in 1:1 correspondence
  // with the ranges in the ConsolidationMap. It can happen, that a range gets split into multiple
  // meshes (if the fragment layouts are not compatible or the mesh would get too larger).
  /** @type {(THREE.Mesh|Number[])[]} */this.meshes = [];

  // for each initially added source geometry, this array provides the position
  // in this.meshes where we can find the corresponding output mesh. The output mesh
  // is either
  //  a) a consolidated mesh that includes the input geometry or
  //  b) a mesh that shares the original material and geometry (if it couldn't be merged)
  this.fragId2MeshIndex = new Int32Array(fragCount);

  // init with -1. This will be overwritten, if a mesh is created for the fragment, but fragments
  // without any associated geometry will keep this
  for (var i = 0; i < this.fragId2MeshIndex.length; i++) {
    this.fragId2MeshIndex[i] = NO_MESH_FOR_FRAGMENT;
  }

  // track summed size
  this.byteSize = 0;

  // Id of the model that has been consolidated
  this.modelId = modelId;

  // keep intermediate result to make reruns faster
  this.consolidationMap = null;
}

Consolidation.prototype = {

  constructor: Consolidation,

  /** Add a consolidation mesh that combines several source geometries.
   *   @param {THREE.BufferGeometry} geom
   *   @param {THREE.Material}       material
   *   @param {number[]}             fragIds         - array of fragment ids associated with this container
   *   @param {number}               [firstFrag]     - Optional: Use (firstFrag, fragCount) to specify
   *   @param {number}               [fragCount]       a range within the fragIds array.
   *   @param {number}               [oldRangeIndex] - Index of the entry in the ranges array
   */
  addContainerMesh: function (geom, material, fragIds, firstFrag, fragCount, oldRangeIndex) {

    // add new mesh
    var newMesh = new THREE.Mesh(geom, material);
    newMesh.modelId = this.modelId;
    this.meshes.push(newMesh);

    // track byte size
    this.byteSize += geom.byteSize;

    // default range: full array
    var rangeStart = firstFrag || 0;
    var rangeLength = fragCount || fragIds.length;
    var rangeEnd = rangeStart + rangeLength;

    // Disable THREE frustum culling for all shapes.
    //
    // Reason:
    // Default frustum culling of THREE.js does not work and would let the mesh disappear.
    // This happens because newMesh.computeBoundingSphere() fails for interleaved vertex buffers.
    // (see Frustum.intersectsObject used in FireFlyWebGLRenderer.projectObject)
    //
    // Instead, we apply culling before passing a mesh to the Renderer. (see ConsolidationIterator.js)
    newMesh.frustumCulled = false;

    // The indices in the meshes array are not in 1:1 correspondence with the indices in the rages array,
    // because processing a single range may result in multiple meshes. Therefore, we store the range index
    // and the start and length of  the range in the generated mesh object.
    newMesh.rangeBegin = firstFrag;
    newMesh.rangeCount = fragCount;
    newMesh.oldRangeIndex = oldRangeIndex;

    // For each source fragment, remember in which container we find it
    var meshIndex = this.meshes.length - 1;
    for (var i = rangeStart; i < rangeEnd; i++) {
      var fragId = fragIds[i];
      this.fragId2MeshIndex[fragId] = meshIndex;
    }
  },

  /**
   *  Add a single mesh that has unique matrix, fragId, and dbId. This is used to add meshes
   *  that share original geometry that could not be merged with anything else.
   *
   *   @param {THREE.BufferGeometry} geom
   *   @param {THREE.Material}      material
   *   @param {number}               fragId
   *   @param {THREE.Matrix4}        matrix
   *   @param {number}               dbId
   */
  addSingleMesh: function (geom, material, fragId, matrix, dbId) {

    // create new mesh
    var newMesh = new THREE.Mesh(geom, material);
    newMesh.matrix.copy(matrix);
    newMesh.matrixAutoUpdate = false;
    newMesh.dbId = dbId;
    newMesh.fragId = fragId;
    newMesh.modelId = this.modelId;

    // add it to mesh array
    this.meshes.push(newMesh);

    // Note: We don't track byteSize for these, because these geometries are shared, i.e., do
    //       not consume any extra memory compared to original geometry.

    // Disable frustum culling (see comment in addContainerMesh)
    newMesh.frustumCulled = false;

    // make it possible to find it later
    this.fragId2MeshIndex[fragId] = this.meshes.length - 1;
  },

  /**
   *  Shortcut to add geometry, material etc. of a single fragment to the consolidation.
   *  This is used for all fragments that could not be combined with others.
   *   @param {number}        fragId
   *   @param {FragmentList}  fragList
   */
  addSingleFragment: function (fragId, fragList) {
    const geometry = fragList.getGeometry(fragId);
    const material = fragList.getMaterial(fragId);
    const dbId = fragList.getDbIds(fragId);

    // Note that the model may be moved using the model transform at any time.
    // We don't want the consolidation computation to be affected by this.
    // Therefore, consolidation is always done with excluded dynamic model transform.
    // The model transform is applied later by the ConsolidationIterator.
    // So, it's important to use the originalWorldMatrix here, which is not affected by model transform changes.
    fragList.getOriginalWorldMatrix(fragId, _tmpMatrix);

    this.addSingleMesh(geometry, material, fragId, _tmpMatrix, dbId);
  },

  /**
   * Create the consolidation geometry for the requested meshIndex
   * @param {Number} meshIndex Index of consolidate/instanced mesh
   * @param {FragmentList} fragList Fragment list for the model
   * @param {Number} [consolidationDeadline] The time (as returned by performance.now) up to which we might perform consolidation
   * @return {THREE.Mesh|null} Consolidate/instanced mesh. If the time didn't suffice
   *                           to compute a consolidated mesh, it will return null.
   */
  createMeshGeometry: function (meshIndex, fragList, consolidationDeadline) {
    var curMesh = this.meshes[meshIndex]; // Current mesh
    var curGeom = curMesh.geometry; // Current geometry

    var consolidationMap = this.consolidationMap;

    // if curGeom is a consolidation placeholder created by _buildConsolidationPlaceholder(),
    //   create actual geometry before proceeding and link it to the mesh
    if (!curGeom.attributes) {
      if (consolidationDeadline !== undefined &&
      performance.now() > consolidationDeadline) {
        return null;
      }

      curMesh.proxyGeometry = curMesh.geometry;
      consolidationMap._buildConsolidationGeometry(curMesh, this, meshIndex, fragList);

      // copy over memory assignment
      curMesh.geometry.streamingDraw = curGeom.streamingDraw;
      curMesh.geometry.streamingIndex = curGeom.streamingIndex;

      // If the out of core tile manager is used, we always must discard the geometry after upload, since it is using the temporary buffer
      curMesh.geometry.discardAfterUpload = USE_OUT_OF_CORE_TILE_MANAGER ? true : curGeom.discardAfterUpload;
      curGeom = curMesh.geometry;
    }

    return curMesh;
  },

  /**
   * Check, whether a consolidated mesh geometry is available for the given meshIndex
   *
   * @param {number} meshIndex - The mesh index
   * @returns {boolean} - True if the mesh geometry is available, false otherwise
   */
  meshGeometryAvailable: function (meshIndex) {
    return this.meshes[meshIndex]?.geometry?.attributes !== undefined;
  },

  /**
   * Returns the nodeId for the given rangeIndex
   *
   * @param {Number} rangeIndex Index of range
   */
  getBvhNodeIdForRange: function (rangeIndex) {
    return this.consolidationMap.bvhNodeIndices[rangeIndex];
  },

  /**
   * Apply the current vizflags and theming colors to the mesh and return it
   * @param {Number} meshIndex Index of consolidate/instanced mesh
   * @param {FragmentList} fragList Fragment list for the model
   * @param {Number} drawMode Render pass id from RenderFlags.
   * @param {Bool} specialHandling True if the mesh needs special handling
   * @param {Number} [consolidationDeadline] The time (as returned by performance.now) up to which we might perform consolidation
   * @return {THREE.Mesh|null} Consolidate/instanced mesh. If the time didn't suffice
   *                           to compute a consolidated mesh, it will return null.
   */
  applyAttributes: function (meshIndex, fragList, drawMode, specialHandling, consolidationDeadline) {
    var curMesh = this.meshes[meshIndex]; // Current mesh
    var curGeom; // Current geometry

    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      // Geometry is  created by the corresponding task
      curGeom = curMesh.geometry;
    } else {
      // If the out-of-core tile manager is not used, we need to create the geometry here
      const mesh = this.createMeshGeometry(meshIndex, fragList, consolidationDeadline);
      curGeom = mesh?.geometry || null;
    }

    // Stop, if no valid geometry is available yet
    if (curGeom === null) {
      return null;
    }

    var consolidationMap = this.consolidationMap;
    var vizflags = fragList.vizflags;

    var fragIds = consolidationMap.fragOrder;
    var instanced = curGeom.numInstances; // Instanced or conslidated
    var rangeStart; // Start of fragment range
    var rangeEnd; // End of fragment range
    var fragId;
    var themingActive = fragList.db2ThemingColor.length > 0 || undefined;

    // Get the range of fragments for the mesh.
    if (instanced) {
      // Instanced buffer. The start of the fragment in fragIds
      // is in the rangeStart property of the mesh. The end is
      // numInstances fragments later.
      rangeStart = curMesh.rangeStart;
      rangeEnd = rangeStart + curMesh.geometry.numInstances;
    } else if (curGeom.attributes.id) {
      // Consolidated buffer - The start ranges are in the
      // consolidated map
      if (USE_OUT_OF_CORE_TILE_MANAGER) {
        let mesh = this.meshes[meshIndex];
        rangeStart = mesh.rangeBegin;
        rangeEnd = mesh.rangeBegin + mesh.rangeCount;
      } else {
        rangeStart = consolidationMap.ranges[meshIndex];
        rangeEnd = meshIndex + 1 >= consolidationMap.ranges.length ?
        consolidationMap.numConsolidated : consolidationMap.ranges[meshIndex + 1];
      }
    } else {
      // No range, just one fragment
      fragId = curMesh.fragId;
    }

    // If the mesh doesn't need special handling, then return it.
    if (!specialHandling || fragId !== undefined) {
      // Clear offsets, but not for single meshes
      if (curGeom.groups && fragId === undefined) {
        curGeom.groups = undefined;
      }
      // set the visibility from the drawMode. Not necessary if we evaluate visibility in the shader
      curMesh.visible = fragId !== undefined || !USE_MULTI_MATERIAL_RENDER_CALLS ? isVisible(vizflags[fragId === undefined ? fragIds[rangeStart] : fragId], drawMode) : true;
      curMesh.themingColor = themingActive && fragList.getThemingColor(fragId);
      return curMesh;
    }
    var start = 0; // Start of current draw call indices
    var end = 0; // End of currend draw call endices - so far
    var startLines = 0; // Start of current draw call indices
    var endLines = 0; // End of currend draw call endices - so far
    var curVisible; // Current draw call visibility
    var curColor; // Current draw call color
    var curDrawCall = 0; // Current draw call index

    // Add a draw call to the consolidated mesh
    function addDrawCall() {
      // If the draw call isn't visible, just skip it
      if (curVisible) {
        curGeom.groups = curGeom.groups || [];
        // Avoid calling addDrawCall because this is inside the draw loop
        // and we would like to reduce the number of object created and
        // released, when possible.
        var offset = curGeom.groups[curDrawCall] || { index: 0 };
        curGeom.groups[curDrawCall++] = offset;
        // Only add the draw call if there is something to draw.
        if (instanced) {
          offset.start = 0;
          offset.count = curGeom.ib ? curGeom.ib.length : curGeom.ibLength;
          if (curGeom.iblines || curGeom.iblinesLength) {
            offset.edgeStart = 0;
            offset.edgeCount = curGeom.iblines ? curGeom.iblines.length : curGeom.iblinesLength;
          }
          offset.instanceStart = start;
          offset.numInstances = end - start;
        } else {
          offset.start = start;
          offset.count = end - start;
          if (curGeom.iblines || curGeom.iblinesLength) {
            offset.edgeStart = startLines;
            offset.edgeCount = endLines - startLines;
          }
        }
        // Set the theming color in the draw call
        offset.themingColor = curColor;
      }
    }

    function addLastDrawCall() {
      if (start === 0) {
        // Only one draw call, Set theming and visibility for entire mesh
        curMesh.themingColor = curColor;
        curMesh.visible = curVisible;
        curMesh.material = Array.isArray(curMesh.material) ? curMesh.material[0] : curMesh.material;
      } else {
        curMesh.visible = true;
        addDrawCall();
        // Clear existing draw calls
      }
      curGeom.groups && (curGeom.groups.length = curDrawCall);
    }

    // Loop through the fragments in the fragment list
    for (var i = rangeStart; i < rangeEnd; ++i) {
      fragId = fragIds[i];

      // Get the visibility and theming color for the fragment
      var visible = isVisible(vizflags[fragId], drawMode);
      var color = themingActive && fragList.getThemingColor(fragId);

      // Skip the first time through the loop
      if (visible !== curVisible || visible && color !== curColor) {
        // Visibility or color change, add a draw call
        if (end > start) {
          addDrawCall();
        }
        // Reset the draw call variables
        start = end;
        startLines = endLines;
        curVisible = visible;
        curColor = color;
      }

      // Add current fragment into the next draw call
      if (instanced) {
        end += 1;
      } else {
        var geom = fragList.getGeometry(fragId);
        end += geom.ib ? geom.ib.length : geom.ibLength;
        if (geom.iblines || geom.iblinesLength) {
          endLines += geom.iblines ? geom.iblines.length : geom.iblinesLength;
        }
      }
    }
    // Add last draw call for the last mesh
    addLastDrawCall();

    return curMesh;
  },

  dispose: function () {
    var DISPOSE_EVENT = { type: 'dispose' };
    var REMOVED_EVENT = { type: 'removed' };

    for (var i = 0; i < this.meshes.length; i++) {
      var mesh = this.meshes[i];
      var geom = mesh?.geometry;
      if (geom) {
        //Both of these are needed -- see also how it's done in FragmentList dispose
        mesh.dispatchEvent(REMOVED_EVENT);
        geom.dispatchEvent(DISPOSE_EVENT);

        // In case of later reuse, setting needsUpdate is essential to render it again.
        geom.needsUpdate = true;
      }
    }

  }
};


/**
 *  @class ConsolidationBuilder is a utility to merge several (usually small) objects into larger ones to
 *  improve rendering performance.
 */
export function ConsolidationBuilder() {
  this.buckets = new Map(); // Map<MergeBucket[]>
  this.bucketCount = 0;
  this.costs = 0; // Consolidation costs in bytes (=costs of merged Geometries for each bucket with >=2 geoms)
}


ConsolidationBuilder.prototype = {

  /**
   *  Add a new Geometry for consolidation. Note that some geometries cannot be merged (e.g., if their material
   *  is different from all others.). In this case, the output mesh just shares input geometry and material.
   *
   *   @param {THREE.BufferGeometry} geom
   *   @param {number}               materialId - id of the material used for this geometry
   *   @param {THREE.Box3}           worldBox - worldBox (including matrix transform!)
   *   @param {Number}               fragId   - used to find out later in which output mesh you find this fragment
   *   @param {Uint32Array}          [fragIdToNodeIdx] - Optional mapping from fragId to BVH node index
   */
  addGeom: function (geom, materialId, worldBox, fragId, fragIdToNodeIdx) {

    // find bucket of meshes that can be merged with the new one
    var bucket = null;
    let id;
    if (USE_MULTI_MATERIAL_RENDER_CALLS) {
      id = fragIdToNodeIdx ? `${fragIdToNodeIdx[fragId]}` : materialId;
    } else {
      id = fragIdToNodeIdx ? `${materialId}_${fragIdToNodeIdx[fragId]}` : materialId;
    }

    var buckets = this.buckets.get(id);
    if (buckets) {
      for (var i = 0; i < buckets.length; i++) {

        // get next bucket
        var nextBucket = buckets[i];

        // compatible primitive type and vertex format?
        var bucketGeom = nextBucket.geoms[0];

        if (geom !== null) {
          // We only check for mergeability and bucket vertex count here, if
          // we are not using the out-of-core tile manager
          // Otherwise, we will have to check these conditions later when
          // building the mesh
          if (!canBeMerged(bucketGeom, geom)) {
            continue;
          }

          // this bucket would allow merging, but only if the vertex count doesn't grow too much
          var vertexCount = getVertexCount(geom);
          if (vertexCount + nextBucket.vertexCount > MaxVertexCountPerMesh) {
            continue;
          }
        }

        // we found a bucket to merge with
        bucket = nextBucket;
        break;
      }
    }

    // create a new bucket to collect this mesh
    if (!bucket) {
      bucket = new MergeBucket(materialId, fragIdToNodeIdx?.[fragId]);
      this.bucketCount++;

      if (!buckets)
      this.buckets.set(id, [bucket]);else

      buckets.push(bucket);
    }

    // add geometry to bucket
    this.costs += bucket.addGeom(geom, worldBox, fragId);
  },

  /**
   * When all geometries have been added to buckets using addGeom() calls, this function converts the buckets into a
   * more compact representation called ConsolidationMap. This map summarizes all information that we need to build
   * the FragmentList consolidation.
   *
   * @param {Uint32Array}    allFragIds      - all fragIds, sorted by consolidation costs.
   * @param {numConsolidate} numConsolidated - number of ids in allFragIds that have been added to consolidation buckets
   *                                           all remaining ones are processed separately by instancing.
   * @returns {ConsolidationMap}
   */
  createConsolidationMap: function (allFragIds, numConsolidated, fragIdToNodeIdx) {

    // init result object
    var fragCount = allFragIds.length;
    var result = new ConsolidationMap(fragCount, this.bucketCount, fragIdToNodeIdx);

    // fill fragOrder and ranges. Each range contains all fragIds of a single bucket
    var nextIndex = 0;
    var bucketIdx = 0;
    this.buckets.forEach((buckets) => {

      for (var b = 0; b < buckets.length; b++) {

        var bucket = buckets[b];

        // store start index of the range in fragOrder that corresponds to this bucket
        result.ranges[bucketIdx] = nextIndex;
        result.bvhNodeIndices[bucketIdx] = bucket.bvhNodeId;

        // store bucket box (no need to copy)
        result.boxes[bucketIdx] = bucket.worldBox;

        // append all fragIds in this bucket
        result.fragOrder.set(bucket.fragIds, nextIndex);

        // move nextIndex to the next range start
        nextIndex += bucket.fragIds.length;
        bucketIdx++;
      }
    });

    // remember which fragIds remain and must be processed by instancing
    result.numConsolidated = numConsolidated;
    for (var i = numConsolidated; i < allFragIds.length; i++) {
      result.fragOrder[i] = allFragIds[i];
    }
    return result;
  }
};

/**
 * A ConsolidationMap is an intermediate result of a FragmentList consolidation. It describes which
 * fragments are to be merged into consolidated meshes and which ones have to be processed by instancing.
 *
 * @param {number} fragCount              - Number of fragments in the model
 * @param {number} bucketCount            - Number of consolidation buckets
 * @param {Uint32Array} [fragIdToNodeIdx] - Optional mapping from fragId to BVH node index
 */
export function ConsolidationMap(fragCount, bucketCount, fragIdToNodeIdx = null, cachedData = null) {

  if (cachedData) {
    this.fragOrder = cachedData.fragOrder;
    this.ranges = cachedData.ranges;
    this.boxes = cachedData.boxes;
    this.numConsolidated = cachedData.numConsolidated;
    this.bvhNodeIndices = cachedData.bvhNodeIndices;
  } else
  {
    // Ordered array of fragIds. Each range of the array defines a merge bucket.
    this.fragOrder = new Uint32Array(fragCount);

    // Offsets into fragOrder. ranges[i] is the startIndex of the range corresponding to merge bucket i.
    this.ranges = new Uint32Array(bucketCount);
    this.bvhNodeIndices = new Uint32Array(bucketCount);

    // Cached bboxes of consolidated meshes
    this.boxes = new Array(bucketCount);

    // Store how many fragIds in fragOrder have been added to merge buckets.
    // (fragIds[0], ..., fragIds[numConsolidated-1].
    this.numConsolidated = -1; // will be set in createConsolidationMap
  }

  // tmp objects
  this.tmpGeoms = [];
  this.tmpMatrix = new THREE.Matrix4();

  // Optional mapping from fragId to BVH node index
  this.fragIdToNodeIdx = fragIdToNodeIdx;

  /** @type {Map<THREE.Material, Map<THREE.Material, boolean>>} */this._compatibleMaterialLUT = new Map();
}

ConsolidationMap.prototype = {

  /**
   * Create consolidated meshes.
   *  @param {FragmentList}    fragList
   *  @param {MaterialManager} matman
   *  @param {RenderModel}     model
   *  @param {boolean}         [useDeferredConsolidation] - If true, only some preparation work is executed immediately. Actual mesh creation happens on rendering.
   *                                                        If false, the system automatically determines if some part is delegated to a
   *                                                        worker thread, so that the blocking time is shorter, or everything happens immediately.
   *  @returns {Consolidation}
   */
  buildConsolidation: function (fragList, matman, model, useDeferredConsolidation = false) {

    const result = new Consolidation(fragList.getCount(), model.getModelId());

    // store this consolidation map with the consolidation, so that we can rebuild it faster.
    result.consolidationMap = this;

    if (useEarlyConsolidation(model)) {
      // with early consolidation, geometry is not available yet. Nothing to do here.
      return result;
    }

    // Init worker thread if enabled
    var parallelMerge = null;

    if (!useDeferredConsolidation) {
      // Check if a worker-implementation is available.
      if (multithreadingSupported()) {
        // Activate multithreaded consolidation
        parallelMerge = new ParallelGeomMerge(result);
      } else {

        //console.warn("Multithreaded consolidation requires to registers worker support. Falling back to single-threaded consolidation.");
      }}

    // each range of fragIds is merged into a consolidated mesh
    for (let c = 0; c < this.ranges.length; c++) {
      this._buildConsolidationMesh(c, fragList, matman, model, useDeferredConsolidation, parallelMerge, result);
    }

    if (parallelMerge) {
      // start workers for geometry merging. This will invoke the worker operations and
      // set result.inProgress to true until all worker results are returned.
      parallelMerge.runTasks();
    }

    return result;
  },

  /**
   * Internal function for creating a single consolidated meshes and adding it to the result.
   *  @param {Number}          rangeIdx                   - index into this.ranges
   *  @param {FragmentList}    fragList
   *  @param {MaterialManager} matman
   *  @param {RenderModel}     model
   *  @param {boolean}         [useDeferredConsolidation] - If true, only some preparation work is executed immediately. Actual mesh creation happens on rendering.
   *                                                        If false, the system automatically determines if some part is delegated to a
   *                                                        worker thread, so that the blocking time is shorter, or everything happens immediately.
   *  @param {ParallelGeomMerge} [parallelMerge]          - if provided and useDeferredConsolidation is false, consolidation tasks are accumulated in that object
   *  @param {Consolidation}   result                     - output
   */
  _buildConsolidationMesh: function (rangeIdx, fragList, matman, model, useDeferredConsolidation, parallelMerge, result) {
    var fragIds = this.fragOrder;
    var rangeCount = this.ranges.length;

    // get range of fragIds in this.fragOrder from which we build the next consolidated mesh.
    // Note that this.ranges only contains the range begins and the last range ends at this.numConsolidated.
    var rangeBegin = this.ranges[rangeIdx];
    var rangeEnd = rangeIdx === rangeCount - 1 ? this.numConsolidated : this.ranges[rangeIdx + 1];
    var rangeLength = rangeEnd - rangeBegin;

    // just 1 shape? => just share original geometry and material
    if (rangeLength === 1) {
      const fragId = fragIds[rangeBegin];
      result.addSingleFragment(fragId, fragList);
      return;
    }

    const subRanges = [];
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      // If we have multiple meshes in the bucket, we might have to further subdivide them into
      // several consolidated meshes. Either because they cannot be merged or because they have
      // too many vertices.
      let subBuckets = [];
      for (let i = rangeBegin; i < rangeEnd; i++) {
        const fragId = fragIds[i];

        // At this point, all geometries should have been loaded and should
        // be available in the fragment list.
        // However, if a selective loading controller is enabled or we had some network issues,
        // it is possible that some fragments didn't get activated. In that case, we ignore the fragment here.
        if (!fragList.isFragmentActive(fragId)) {
          continue;
        }

        const geom = fragList.getGeometry(fragId);
        const mat = fragList.getMaterial(fragId);

        // find bucket of meshes that can be merged with the new one
        let foundSubBucket = null;
        let vertexCount = getVertexCount(geom);
        for (let j = 0; j < subBuckets.length; j++) {

          // get next bucket
          const subBucket = subBuckets[j];

          // compatible primitive type and vertex format?
          const subBucketGeom = subBucket.geoms[0];

          // We only check for mergeability and bucket vertex count here, if
          // we are not using the out-of-core tile manager
          // Otherwise, we will have to check these conditions later when
          // building the mesh
          if (!canBeMerged(subBucketGeom, geom)) {
            continue;
          }

          if (USE_MULTI_MATERIAL_RENDER_CALLS) {
            if (isSupportedUBOMaterial(mat)) {// only check for compatibility if we the materials supports merging
              if (subBucket.fragIDs.length >= MAX_FRAGMENTS_PER_CONSOLIDATED_MESH ||
              subBucket.materialIds.size >= getMaxMaterialsPerUBO(subBucket.material) ||
              !materialsAreUBOCompatible(subBucket.material, mat, this._compatibleMaterialLUT)) {
                continue; // look for next bucket if it cannot be merged into this one
              }
            } else {
              // for non-UBO materials, we can only merge if the material is the same
              if (subBucket.material !== mat)
              continue;
            }
          }

          // this bucket would allow merging, but only if the vertex count doesn't grow too much
          if (vertexCount + subBucket.vertexCount > MaxVertexCountPerMesh) {
            continue;
          }

          foundSubBucket = subBucket;
          break;
        }

        if (foundSubBucket) {
          foundSubBucket.geoms.push(geom);
          foundSubBucket.fragIDs.push(fragId);
          foundSubBucket.vertexCount += vertexCount;
          foundSubBucket.materialIds.add(mat.id);
        } else {
          subBuckets.push({
            geoms: [geom],
            fragIDs: [fragId],
            vertexCount: vertexCount,
            material: mat,
            materialIds: new Set([mat.id])
          });
        }
      }

      let index = rangeBegin;
      for (let i = 0; i < subBuckets.length; i++) {
        fragIds.set(subBuckets[i].fragIDs, index);
        subRanges.push([index, subBuckets[i].fragIDs.length]);
        index += subBuckets[i].fragIDs.length;
      }

    } else {
      subRanges.push([rangeBegin, rangeLength]);
    }

    for (const subRange of subRanges) {
      let mergedGeom = null;
      if (useDeferredConsolidation) {
        mergedGeom = this._buildConsolidationPlaceholder(subRange[0], subRange[1], fragIds, fragList);
      } else {
        mergedGeom = this._buildConsolidationGeometryImpl(result, rangeIdx, subRange[0], subRange[1], fragIds, fragList, parallelMerge);
      }
      mergedGeom.nodeIdx = this.bvhNodeIndices[rangeIdx];

      // use material of first frag in the bucket
      var firstFrag = fragIds[subRange[0]];
      var material = fragList.getMaterial(firstFrag);

      var newMaterial = matman.getMaterialVariant(material, MATERIAL_VARIANT.VERTEX_IDS, model);

      // add result
      result.addContainerMesh(mergedGeom, newMaterial, fragIds, subRange[0], subRange[1], rangeIdx);
    }

  },

  /**
   * Internal function for creating a single consolidated geometry.
   *  @param {THREE.Mesh}      mesh                       - mesh object containing the geometry
   *  @param {Consolidation}   consolidation              - Consolidation object
   *  @param {Number}          rangeIdx                   - index into this.ranges
   *  @param {FragmentList}    fragList
   *  @param {ParallelGeomMerge} [parallelMerge]          - if provided consolidation tasks are accumulated in that object
   *  @returns {BufferGeometry}
   */
  _buildConsolidationGeometry: function (mesh, consolidation, rangeIdx, fragList, parallelMerge) {
    var fragIds = this.fragOrder;

    // get range of fragIds in this.fragOrder from which we build the next consolidated mesh.
    let rangeBegin = mesh.rangeBegin;
    let rangeLength = mesh.rangeCount;

    if (!USE_OUT_OF_CORE_TILE_MANAGER && rangeLength === 1) {
      // just 1 shape? => just share original geometry and material
      return;
    }

    mesh.geometry = this._buildConsolidationGeometryImpl(consolidation, mesh.oldRangeIndex, rangeBegin, rangeLength, fragIds, fragList, parallelMerge);
  },

  /**
   * Very internal function for creating a single consolidated geometry from a given range.
   *  @param {Consolidation}   consolidation              - Consolidation object
   *  @param {Number}          rangeIdx                   - index into this.ranges
   *  @param {Number}          rangeBegin                 - first fragment in fragIds
   *  @param {Number}          rangeLength                - length of this range
   *  @param {Uint32Array}     fragIds                    - list of fragIds, from which the range is taken
   *  @param {FragmentList}    fragList
   *  @param {ParallelGeomMerge} [parallelMerge]          - if provided consolidation tasks are accumulated in that object
   *  @returns {BufferGeometry}
   */
  _buildConsolidationGeometryImpl: function (consolidation, rangeIdx, rangeBegin, rangeLength, fragIds, fragList, parallelMerge) {

    // create array of BufferGeometry pointers
    this.tmpGeoms.length = rangeLength;

    // create Float32Array containing the matrix per src fragment
    var matrices = new Float32Array(16 * rangeLength);

    // create Int32Array of dbIds
    var dbIds = new Uint32Array(rangeLength);

    let fragId;
    for (var i = 0; i < rangeLength; i++) {
      fragId = fragIds[rangeBegin + i];

      // fill geoms
      this.tmpGeoms[i] = fragList.getGeometry(fragId);

      // store matrix as 16 floats
      fragList.getOriginalWorldMatrix(fragId, this.tmpMatrix);
      matrices.set(this.tmpMatrix.elements, 16 * i);

      // store dbId in Int32Array
      dbIds[i] = USE_MULTI_MATERIAL_RENDER_CALLS ? i : fragList.getDbIds(fragId);
    }

    // get box of consolidated mesh
    var box = this.boxes[rangeIdx];
    var mergedGeom = mergeGeometries(this.tmpGeoms, matrices, dbIds, box, parallelMerge);

    // Make sure, that consolidated meshes are always kept on the GPU
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      mergedGeom.streamingDraw = false;
      mergedGeom.streamingIndex = false;
    }

    return mergedGeom;
  },

  /**
   * Very internal function for creating a single placeholder for consolidated geometry from a given range.
   *  @param {Number}          rangeBegin                 - first fragment in fragIds
   *  @param {Number}          rangeLength                - length of this range
   *  @param {Uint32Array}     fragIds                    - list of fragIds, from which the range is taken
   *  @param {FragmentList}    fragList
   *  @returns {Object}
   */
  _buildConsolidationPlaceholder: function (rangeBegin, rangeLength, fragIds, fragList) {
    // compute byteSize (for memory assignment)
    let byteSize = 0;
    for (let i = 0; i < rangeLength; i++) {
      const fragId = fragIds[rangeBegin + i];

      // fill geoms
      const geom = fragList.getGeometry(fragId);
      byteSize += getByteSize(geom);
    }

    // create temporary "geometry" (to be replaced on first use)
    // need to define dispatchEvent in case the geometry is never uploaded
    const mergedGeom = { byteSize, rangeStart: rangeBegin, rangeCount: rangeLength, dispatchEvent: function () {} };
    return mergedGeom;
  }
};

function multithreadingSupported() {
  return !!ParallelGeomMerge.createWorker;
}

/*
 * A too fine-grained BVH may neutralize the performance gain by consolidation. To avoid that, use these defaults
 * for bvh settings when consolidation is wanted. Model loaders do this automatically when useConsolidation is set to true.
 *  @param {Object} bvhOptions
 */
Consolidation.applyBVHDefaults = function (bvhOptions) {
  bvhOptions["frags_per_leaf_node"] = 512;
  bvhOptions["max_polys_per_node"] = 100000;
};